import { API_CONFIG } from '../config/api.config';
import { storageService } from '../utils/storage';
import { buildStrapiQuery, type QueryParams } from './api-query-builder';

export * from './api-query-builder';

export interface ApiResponse<T = any> {
  data: T;
  meta?: {
    pagination?: {
      page: number;
      pageSize: number;
      pageCount: number;
      total: number;
    };
  };
}

export interface ApiError {
  error: {
    status: number;
    name: string;
    message: string;
    details?: any;
  };
}

export interface RequestOptions extends RequestInit {
  headers?: HeadersInit;
  params?: Record<string, any>;
  timeout?: number;
  skipAuth?: boolean;
}

class ApiService {
  private baseUrl: string;
  private defaultHeaders: HeadersInit;
  private isRefreshingToken: boolean = false;
  private refreshTokenPromise: Promise<string> | null = null;

  constructor() {
    this.baseUrl = `${API_CONFIG.BASE_URL}${API_CONFIG.API_VERSION}`;
    this.defaultHeaders = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    };
  }

  private buildHeaders(options?: RequestOptions): HeadersInit {
    const headers: HeadersInit = { ...this.defaultHeaders };

    if (!options?.skipAuth) {
      const token = storageService.getToken();
      if (token) {
        (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
      }
    }

    const language = storageService.getLanguage();
    (headers as Record<string, string>)['Accept-Language'] = language;

    if (options?.headers) {
      Object.assign(headers, options.headers);
    }

    return headers;
  }

  private forceLogout(): void {
    console.error('🚪 [API] Force logout - token expired and refresh failed');
    storageService.clearAuth();
    window.location.href = '/login';
  }

  private async refreshAuthToken(): Promise<string> {
    if (this.refreshTokenPromise) {
      return this.refreshTokenPromise;
    }

    this.refreshTokenPromise = (async () => {
      try {
        console.log('🔄 [API] Attempting to renew token...');
        
        const token = storageService.getToken();
        if (!token) {
          console.warn('⚠️ [API] No token found for renewal');
          this.forceLogout();
          throw new Error('No token to renew');
        }

        const url = `${API_CONFIG.BASE_URL}/admin/access-token`;
        const response = await fetch(url, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          console.error('❌ [API] Token renewal returned status:', response.status);
          this.forceLogout();
          throw new Error('Token renewal failed');
        }

        const data = await response.json();
        const newToken = data.data?.token;

        if (!newToken) {
          console.error('❌ [API] No new token in renewal response');
          this.forceLogout();
          throw new Error('No token in renewal response');
        }

        storageService.setToken(newToken);
        console.log('✅ [API] Token renewed successfully');

        return newToken;
      } catch (error) {
        console.error('❌ [API] Token renewal error:', error);
        this.forceLogout();
        throw error;
      } finally {
        this.refreshTokenPromise = null;
      }
    })();

    return this.refreshTokenPromise;
  }

  private async handleError(response: Response): Promise<never> {
    let error: ApiError;

    try {
      error = await response.json();
    } catch {
      error = {
        error: {
          status: response.status,
          name: 'NetworkError',
          message: response.statusText || 'Network error occurred',
        },
      };
    }

    if (response.status === API_CONFIG.STATUS_CODES.UNAUTHORIZED) {
      console.warn('⚠️ [API] 401 Unauthorized - Token may be expired');
    }

    throw error;
  }

  private async request<T = any>(
    endpoint: string,
    options?: RequestOptions,
    isRetry: boolean = false
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseUrl}${endpoint}`;
    const headers = this.buildHeaders(options);

    const config: RequestInit = {
      ...options,
      headers,
    };

    const timeout = options?.timeout || API_CONFIG.TIMEOUT;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(url, {
        ...config,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      // Handle 401 Unauthorized - Try to renew token and retry once
      if (response.status === API_CONFIG.STATUS_CODES.UNAUTHORIZED && !isRetry) {
        console.log('🔑 [API] Received 401, attempting token renewal...');
        
        try {
          await this.refreshAuthToken();
          console.log('🔄 [API] Retrying request with renewed token...');
          return await this.request<T>(endpoint, options, true);
        } catch (refreshError) {
          // refreshAuthToken already calls forceLogout on failure
          throw refreshError;
        }
      }

      // If retry also got 401, the new token is also invalid - force logout
      if (response.status === API_CONFIG.STATUS_CODES.UNAUTHORIZED && isRetry) {
        console.error('❌ [API] Retry with new token still got 401 - forcing logout');
        this.forceLogout();
      }

      if (!response.ok) {
        return this.handleError(response);
      }

      // Handle 204 No Content
      if (response.status === API_CONFIG.STATUS_CODES.NO_CONTENT) {
        return { data: null as any };
      }

      const data = await response.json();
      return data;
    } catch (error: any) {
      clearTimeout(timeoutId);

      if (error.name === 'AbortError') {
        const timeoutError = new Error('Request timeout');
        (timeoutError as any).status = 408;
        (timeoutError as any).error = {
          status: 408,
          name: 'TimeoutError',
          message: 'Request timeout',
        };
        throw timeoutError;
      }

      throw error;
    }
  }

  async get<T = any>(endpoint: string, params?: QueryParams, options?: RequestOptions): Promise<ApiResponse<T>> {
    const query = buildStrapiQuery(params);
    return this.request<T>(`${endpoint}${query}`, {
      method: 'GET',
      ...options,
    });
  }

  async post<T = any>(endpoint: string, data?: any, options?: RequestOptions): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify({ data }),
      ...options,
    });
  }

  async put<T = any>(endpoint: string, data?: any, options?: RequestOptions): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify({ data }),
      ...options,
    });
  }

  async delete<T = any>(endpoint: string, options?: RequestOptions): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
      ...options,
    });
  }

  async upload<T = any>(endpoint: string, formData: FormData, options?: RequestOptions): Promise<ApiResponse<T>> {
    const headers = this.buildHeaders(options);
    delete (headers as Record<string, string>)['Content-Type'];

    return this.request<T>(endpoint, {
      method: 'POST',
      body: formData,
      headers,
      ...options,
    });
  }

  async download(endpoint: string, filename?: string, options?: RequestOptions): Promise<void> {
    const url = `${this.baseUrl}${endpoint}`;
    const headers = this.buildHeaders(options);

    const response = await fetch(url, { headers });

    if (!response.ok) {
      return this.handleError(response);
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename || 'download';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  }
}

export const apiService = new ApiService();
export default apiService;
